// Queries de la pantalla de planta (P1) y su configuración (C15).
//
// Feature-local (mismo criterio que mantenimiento/queries.ts): mismo cliente
// apiGet/apiPut, contratos Zod compartidos, keys colgadas de ['pantalla'].
//
// La TV es una superficie EN VIVO: polling de 30 s (misma política que los
// dashboards operativos, ver lib/api/queries.ts §política de staleTime); 10 s
// si la planta tiene líneas conectadas a máquina (R12, `tiene_lineas_maquina`).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  configPantallaCoordinadorSchema,
  configPantallaSchema,
  pantallaDatosSchema,
  type ConfigPantallaUpdate,
} from 'schemas';
import { apiGet, apiPut } from '../../lib/api/client';
import { intervaloPantalla } from '../../lib/api/intervalo-vivo';

export const pantallaKeys = {
  datos: (plantaId: string | null) => ['pantalla', 'datos', plantaId ?? 'propia'] as const,
  config: (plantaId: string | null) => ['pantalla', 'config', plantaId ?? 'primera'] as const,
};

/** Payload completo de la TV. plantaId solo aplica en vista previa (coordinador/directivo). */
export function usePantallaDatos(plantaId: string | null) {
  const qs = plantaId ? `?planta_id=${plantaId}` : '';
  return useQuery({
    queryKey: pantallaKeys.datos(plantaId),
    queryFn: () => apiGet(`/api/pantalla/datos${qs}`, pantallaDatosSchema),
    refetchInterval: (query) => intervaloPantalla(query.state.data),
    staleTime: 25_000,
  });
}

/** Config + plantas visibles para el editor C15 del coordinador. */
export function useConfigPantalla(plantaId: string | null) {
  const qs = plantaId ? `?planta_id=${plantaId}` : '';
  return useQuery({
    queryKey: pantallaKeys.config(plantaId),
    queryFn: () =>
      apiGet(`/api/coordinador/configuracion/pantalla${qs}`, configPantallaCoordinadorSchema),
    staleTime: 60_000,
  });
}

export function useGuardarConfigPantalla() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigPantallaUpdate) =>
      apiPut('/api/coordinador/configuracion/pantalla', body, configPantallaSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pantalla'] });
    },
  });
}
